import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog'
import { EmptyState, PageHeader } from '@/components/page'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { permitKeys, recordInbound, unmatchedQuery } from '@/features/permits/api'
import { requireNav } from '@/nav/guard'

export const Route = createFileRoute('/_app/permits')({
  beforeLoad: requireNav('permits'),
  component: PermitsPage,
})

/** Antragspostfach (nur GU): nicht zugeordnete Eingänge der Behörden und manuelles Erfassen eines Eingangs. */
function PermitsPage() {
  const { t, i18n } = useTranslation()
  const unmatched = useQuery(unmatchedQuery)
  const [recording, setRecording] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )

  return (
    <>
      <PageHeader title={t('pages.permits.title')} description={t('pages.permits.description')}>
        <Button type="button" onClick={() => setRecording(true)}>
          {t('permits.mailbox.record')}
        </Button>
      </PageHeader>
      {notice && (
        <p role="status" className="mb-3 text-sm">
          {notice}
        </p>
      )}
      <h2 className="mb-2 text-lg font-medium">{t('permits.mailbox.unmatchedTitle')}</h2>
      {unmatched.isPending ? (
        <Skeleton className="h-16 w-full" aria-label={t('common.loading')} />
      ) : unmatched.isError ? (
        <EmptyState title={t('permits.loadError')} />
      ) : unmatched.data.length === 0 ? (
        <EmptyState title={t('permits.mailbox.emptyTitle')} body={t('permits.mailbox.emptyBody')} />
      ) : (
        <ul className="grid gap-3" aria-label={t('permits.mailbox.unmatchedTitle')}>
          {unmatched.data.map((message) => (
            <li key={message.id} className="rounded-lg border bg-card p-4">
              <p className="font-medium">{message.subject}</p>
              <p className="text-sm text-muted-foreground">
                {t(`permits.messageKind.${message.kind}`)} · {format(message.occurredAt)} ·{' '}
                {message.provider}
              </p>
            </li>
          ))}
        </ul>
      )}
      {recording && <RecordDialog onClose={() => setRecording(false)} onDone={setNotice} />}
    </>
  )
}

function RecordDialog({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (message: string) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const ids = { subject: useId(), kind: useId(), body: useId() }
  const [subject, setSubject] = useState('')
  const [kind, setKind] = useState<'RECEIPT' | 'DECISION' | 'OTHER'>('RECEIPT')
  const [body, setBody] = useState('')
  return (
    <ConfirmActionDialog
      open
      onOpenChange={(open) => !open && onClose()}
      actionLabel={t('permits.mailbox.record')}
      description={t('permits.mailbox.recordHint')}
      confirmDisabled={subject.trim() === ''}
      onConfirm={async () => {
        const message = await recordInbound({
          subject: subject.trim(),
          kind,
          ...(body.trim() ? { body: body.trim() } : {}),
        })
        await queryClient.invalidateQueries({ queryKey: permitKeys.all })
        onDone(
          t(message.status === 'MATCHED' ? 'permits.mailbox.matched' : 'permits.mailbox.unmatched'),
        )
      }}
    >
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor={ids.subject}>{t('permits.mailSubject')}</Label>
          <Input id={ids.subject} value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={ids.kind}>{t('permits.mailbox.kind')}</Label>
          <select
            id={ids.kind}
            className="h-11 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'RECEIPT' | 'DECISION' | 'OTHER')}
          >
            {(['RECEIPT', 'DECISION', 'OTHER'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`permits.messageKind.${value}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={ids.body}>{t('permits.mailBody')}</Label>
          <Textarea id={ids.body} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      </div>
    </ConfirmActionDialog>
  )
}
